import { Coordinate } from 'ol/coordinate';
import * as olSphere from 'ol/sphere';
import * as olProj from 'ol/proj';

import { SegmentPoint } from '../../models/segment-point';

export class SegmentUtilities {

    standardDistance = 20;
    rest = 0;
    distanceFromStart = 0;
    input: Coordinate[];
    output: SegmentPoint[];
    srcProjection: olProj.Projection;
    idCounter: number = 0;

    constructor(input: Coordinate[], projection: olProj.Projection) {
        this.input = input;
        this.output = [];
        this.srcProjection = projection;
    }

    calculatePoints(standardDistance: number) {
        this.standardDistance = standardDistance;
        for (let i = 0; i < this.input.length - 1; i++) {
            let pt_i = this.input[i];
            let pt_f = this.input[i + 1];
            let distance_0_f = this.getDistance(pt_i, pt_f);
            // angle between X ax and segment from pt_i (as geom 0,0) and pt_f
            let angle: number = this.getAngle(pt_i, pt_f);
            // how many additional points at distance standardDistance are in the segment
            let numberOfExtraPoints = Math.floor((distance_0_f - this.rest) / this.standardDistance);
            this.setNextSegment(pt_i, pt_f, angle, numberOfExtraPoints);
        }
        this.putFinalPoint();
        return this.output;
    }

    getAngle(pt_i: Coordinate, pt_f: Coordinate): number {
        let delta_x = pt_f[0] - pt_i[0];
        let delta_y = pt_f[1] - pt_i[1];
        let angle: number;

        if (delta_x > 0) {
            if (delta_y > 0) {
                angle = Math.atan(delta_y / delta_x);
            } else if (delta_y < 0) {
                angle = 2 * Math.PI + Math.atan(delta_y / delta_x);
            } else { // delta_y == 0
                angle = 0;
            }
        } else if (delta_x < 0) {
            if (delta_y != 0) {
                angle = Math.PI + Math.atan(delta_y / delta_x);
            } else { // delta_y == 0
                angle = Math.PI;
            }
        } else { // delta_x == 0
            if (delta_y > 0) {
                angle = Math.PI / 2;
            } else if (delta_y < 0) {
                angle = 3 * Math.PI / 2;
            } else { // delta_y == 0
                angle = 0;
            }
        }
        return angle;
    }

    /*
        we create a new segment with points situated at standardDistance from its neighbours
     */
    private setNextSegment(pt_i: Coordinate, pt_f: Coordinate, angle: number, numberOfExtraPoints: number) {

        // no extra point
        if (numberOfExtraPoints <= 0) {
            const dist = this.getDistance(pt_f, pt_i);
            // the remaining distance is too short to set a first point
            if (this.rest > dist) {
                this.rest = this.rest - dist;
                return
            }
        }

        // if a rest of standardDistance remains from the previous segment, add it to the first point
        let pt_first: SegmentPoint;
        if (this.rest == 0) {
            pt_first = new SegmentPoint(this.idCounter++, pt_i[0], pt_i[1]);
        } else {
            // we set the first point at a point at pt_i + rest
            let deltas_first = this.calculateCoordsDelta(this.rest, angle);
            pt_first = new SegmentPoint(this.idCounter++, pt_i[0] + deltas_first[0], pt_i[1] + deltas_first[1]);
            this.distanceFromStart += this.standardDistance;
            pt_first.setDistanceFromStart(this.distanceFromStart);
        }
        this.output.push(pt_first);

        let pt_next: SegmentPoint = new SegmentPoint(0, pt_first.getX(), pt_first.getY(), 0, 0, 0);
        let deltas_next = this.calculateCoordsDelta(this.standardDistance, angle);
        for (let i = 1; i <= numberOfExtraPoints; i++) {
            pt_next = new SegmentPoint(this.idCounter++, pt_first.getX() + i * deltas_next[0], pt_first.getY() + i * deltas_next[1]);
            this.output.push(pt_next);
            this.distanceFromStart += this.standardDistance;
            pt_next.setDistanceFromStart(this.distanceFromStart);
        }
        // we calculate the rest as the distance between the last calculated point and the extremity of the input segment because it is closer than standardDistance
        this.rest = Math.abs(this.standardDistance - this.getDistance(pt_f, pt_next.getCoordinate()));

    }

    private calculateCoordsDelta(deltaDistance: number, angle: number) {
        let deltas: number[] = [];
        let deltaX = Math.cos(angle) * deltaDistance;
        let deltaY = Math.sin(angle) * deltaDistance;
        deltas[0] = deltaX;
        deltas[1] = deltaY;
        return deltas;
    }

    private putFinalPoint() {
        if (this.rest > 0) {
            const finalPoint = this.input[this.input.length - 1];
            const finalSegmentPoint = new SegmentPoint(this.idCounter++, finalPoint[0], finalPoint[1], 0, 0, 0);
            const lastCalculatedPoint = this.output[this.output.length - 1];
            const lastDistance = this.getDistance([lastCalculatedPoint.getX(), lastCalculatedPoint.getY()], finalPoint);
            this.distanceFromStart += Math.round(lastDistance);
            finalSegmentPoint.setDistanceFromStart(this.distanceFromStart);
            this.output.push(finalSegmentPoint);
        }
    }

    getDistance(origin: Coordinate, destination: Coordinate) {
        const p1 = olProj.transform(origin, this.srcProjection, 'EPSG:4326');
        const p2 = olProj.transform(destination, this.srcProjection, 'EPSG:4326');
        let distance: number = olSphere.getDistance(p1, p2);
        return distance;
    }

    radiansToDegrees(rad: number) {
        return rad * (180.0 / Math.PI);
    }

}